import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "inline-flex text-xs items-center justify-center whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-transparent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 font-semibold",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input hover:bg-accent hover:text-accent-foreground",
        "outline-secondary-light":
          "border border-secondary/80 hover:bg-secondary/80 hover:text-secondary-foreground text-secondary/80",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        frosty:
          "backdrop-blur-[2px] bg-white/30 border border-solid border-white/50 hover:bg-primary/10 shadow-[0_0px_0.5px_1px_white] text-white",
        "dark-clear-resin":
          "bg-slate-600/70 backdrop-blur-[0.0625rem] focus:bg-slate-800/50 focus:backdrop-blur-0 hover:bg-slate-600/50 text-white border-[0.03125rem] border-solid border-white",
        tertiary:
          "bg-[#FFFFFF] border border-solid border-[#5A6A93]/50 text-[#5A6A93]",
      },
      size: {
        default: "rounded-md h-9 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-9 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
